<template>
  <v-row no-gutters>
    <v-col cols="12">
      <v-row justify="space-between" align="center" no-gutters>
        <v-col cols="auto">
          <dots-menu
            :items="dotsItems"
            @modificationGeometry="setSuggestionType('geom_modify')"
            @newGeometry="setSuggestionType('geom_new')"
          >
            <template #activator="{ on }">
              <data-input
                v-on="getListeners(on)"
                name="suggestion"
                ref="suggestionInput"
                v-model="payload['geometry']"
                :data-type="{ name: 'geometry' }"
                :button-props="{ translationPath: 'dialog.suggestChange' }"
                :geometry-type="geometryType"
                :snapping-active-layer-id="layerId"
                snapping
              />
            </template>
          </dots-menu>
        </v-col>
        <v-col cols="auto" v-if="!!payload['geometry']">
          <v-tooltip color="font" top>
            <template v-slot:activator="{ on }">
              <v-btn v-on="on" color="success" text icon @click="openDialog('Suggestion')">
                <v-icon>mdi-check</v-icon>
              </v-btn>
            </template>
            <span> {{ $i18n.t('button.suggestChange') }} </span>
          </v-tooltip>
          <v-tooltip color="font" top>
            <template v-slot:activator="{ on }">
              <v-btn v-on="on" color="error" text icon @click="openDialog('Reject')">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </template>
            <span> {{ $i18n.t('button.rejectChange') }} </span>
          </v-tooltip>
        </v-col>
      </v-row>
    </v-col>
    <main-large-dialog
      v-if="isDialogSuggestionLoaded"
      :is-visible.sync="isDialogSuggestionVisible"
      :title="$i18n.t('dialog.title.suggestGeometryChange')"
      agree-text="dialog.agreeSuggest"
      :loading="isDialogLoading"
      is-buttons-right
      @agree="suggestChangeHandler"
    >
      <template #body>
        <v-col class="text-left mb-5">
          {{ $i18n.t('dialog.body.suggestChangeConfirmation') }}
        </v-col>
        <v-col cols="12">
          <data-input
            v-model="payload['description']"
            :data-type="{ name: 'textarea' }"
            :label="$i18n.t('dialog.comment')"
            rows="5"
            hide-details
            clearable
          />
        </v-col>
      </template>
    </main-large-dialog>
    <main-dialog
      v-if="isDialogRejectLoaded"
      :is-visible.sync="isDialogRejectVisible"
      :title="$i18n.t('dialog.title.rejectChange')"
      :body="$i18n.t('dialog.body.cancelSuggestionConfirmation')"
      agree-text="dialog.discard"
      :loading="isDialogLoading"
      @agree="rejectChangeHandler"
    />
  </v-row>
</template>

<script>
import { call, get } from 'vuex-pathify';

import DotsMenu from '@/components/DotsMenu';
import DataInput from '@/components/DataInput';

export default {
  name: 'SuggestionsInput',
  components: {
    DotsMenu,
    DataInput,
    MainDialog: () => import('@/components/MainDialog'),
    MainLargeDialog: () => import('@/components/MainLargeDialog'),
  },
  props: {
    featureId: {
      type: [String, Number],
      required: true,
    },
    dataSourceName: {
      type: String,
      required: true,
    },
    layerId: {
      type: [Number, String],
      required: true,
    },
    geometry: {
      type: Object,
      default: null,
    },
  },
  data: () => ({
    isDialogSuggestionLoaded: false,
    isDialogSuggestionVisible: false,
    isDialogRejectLoaded: false,
    isDialogRejectVisible: false,
    isDialogLoading: false,
    payload: {
      description: '',
      suggestion_type: '',
      geometry: null,
    },
  }),
  computed: {
    dataSource: get('layers/metadata@:dataSourceName'),
    geometryType() {
      return this.dataSource.geometry_type;
    },
    dotsItems() {
      return [
        { name: 'modificationGeometry', disabled: !!this.payload.geometry },
        { name: 'newGeometry', disabled: !!this.payload.geometry },
      ];
    },
  },
  methods: {
    addSuggestion: call('suggestions/addSuggestion'),
    getListeners(on) {
      return !!this.$refs?.suggestionInput?.$refs['data-input'].activeType && !this.payload['geometry']
        ? {
            click: this.toggleOff,
          }
        : on;
    },
    toggleOff() {
      this.$refs?.suggestionInput?.$refs['data-input']?.toggleOff();
      this.payload = {
        description: '',
        suggestion_type: '',
        geometry: null,
      };
    },
    async openDialog(dialogType) {
      this[`isDialog${dialogType}Loaded`] = true;
      this[`isDialog${dialogType}Visible`] = true;
    },
    async suggestChangeHandler() {
      this.isDialogLoading = true;
      try {
        const payload = {
          datasource: this.dataSourceName,
          feature: this.featureId,
          ...this.payload,
        };
        await this.addSuggestion(payload);
        this.toggleOff();
      } finally {
        this.isDialogLoading = false;
        this.isDialogSuggestionLoaded = false;
        this.isDialogSuggestionVisible = false;
      }
    },
    rejectChangeHandler() {
      this.toggleOff();
      this.isDialogRejectLoaded = false;
      this.isDialogRejectVisible = false;
    },

    setSuggestionType(type) {
      this.payload['suggestion_type'] = type;
      if (this.payload.geometry) return;
      if (type !== 'geom_new') {
        this.payload['geometry'] = this.geometry;
      }
      this.$refs?.suggestionInput?.$refs['data-input']?.toggleOn('drawGeometry');
    },
  },
};
</script>
